import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfPen,
  PdfBrush,
  PdfColor,
  PdfFont,
  ETextAlign,
  PdfImage,
} from '../../pdf/pdf';
import { ILayoutImageSingleGen } from '../layoutImageSingle';
import { OptsImageSingle } from '../optsImageSingle';
import { IItemCount } from '@educraft/base';
import { GUtil } from '../../pdf/gutil';
import { PoolDict } from '../../core/poolDict';
import { QSingle } from '@educraft/base';
import { fixGenArray } from '../../core/genHelper';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/image';

const POOL_SIZE = 1000;
const MIN_ANSWERS = 6;

/**
 * Pocitani_hex (Counting Hexagons) - Hexagonal equation puzzles
 *
 * Layout: One portrait page per image.
 * Each page: honeycomb of 7 hexagons (2-3-2 rows).
 * Center hexagon shows the answer number in large font.
 * Outer hexagons show 6 different equations that equal the answer.
 * Each hexagon has the page's image behind a semi-transparent overlay.
 *
 * Uses PoolDict to group generated equations by answer.
 * Each bucket must have at least 6 question variants for the outer hexagons.
 *
 * Migrated from C# EduFrame.Img10.Generators.Pocitani_hex
 */
export class PocitaniHex implements ILayoutImageSingleGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsImageSingle): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);

    const d = (PdfSize.A4.width - 2 * margin) / 6;
    const r = (2 * d) / Math.sqrt(3);

    const originX = margin + d;
    const originY = PdfSize.A4.height - 5 * r;

    const bigFont = new PdfFont(PdfFont.FONT_NAME, 160);

    const pool = new PoolDict<QSingle>(
      (q) => q.answer,
      (q) => q.texts.join(' ')
    );

    for (let i = 0; i < POOL_SIZE; i++) {
      pool.add(opts.generator.generate(opts.data));
    }

    const validAnswers = pool.getBucketKeys(MIN_ANSWERS);

    for (let imgIdx = 0; imgIdx < opts.list.length; imgIdx++) {
      const page = pdf.addPage();

      const answerKey = validAnswers[imgIdx % validAnswers.length];
      const questions = pool.getBucket(answerKey);

      const answer = parseInt(answerKey, 10);

      let hexIndex = 0;
      for (let y = 0; y < 3; y++) {
        const count = 3 - (1 - (y % 2));
        for (let x = 0; x < count; x++) {
          const px = originX + (x * 2 + (1 - (y % 2))) * d;
          const py = originY + (r / 2) * 3 * y;

          let font: PdfFont = GUtil.baseFont;
          let label: string;

          if (hexIndex === 3) {
            font = bigFont;
            label = `${answer}`;
          } else {
            const qIndex = hexIndex < 3 ? hexIndex : hexIndex - 1;
            label = fixGenArray(questions[qIndex].texts).join(' ');
          }

          this.drawHex(
            page,
            opts.list[imgIdx].image,
            new PdfPoint(px, py),
            r,
            hexIndex,
            font,
            label
          );
          hexIndex++;
        }
      }
    }
  }

  private drawHex(
    page: ReturnType<PdfGen['addPage']>,
    image: PdfImage | null,
    p: PdfPoint,
    r: number,
    index: number,
    font: PdfFont,
    label: string
  ): void {
    const points: PdfPoint[] = [];
    for (let i = 0; i < 6; i++) {
      points.push(
        new PdfPoint(
          p.x + r * Math.sin((i * Math.PI * 2) / 6),
          p.y + r * Math.cos((i * Math.PI * 2) / 6)
        )
      );
    }

    const sz = r * 1.2;
    const rect = new PdfRect(p.x - sz / 2, p.y - sz / 2, sz, sz);

    if (image) {
      page.drawImageCenter(image, rect);
    }

    const brush =
      index === 3
        ? new PdfBrush(PdfColor.fromCmyk(0.2, 0, 0, 0, 0))
        : new PdfBrush(PdfColor.fromCmyk(0.6, 0, 0, 0, 0));

    page.drawPolygon(null, brush, points);

    const whitePen = index === 3 ? null : GUtil.outlinePen;
    page.drawString(label, font, PdfBrush.Black, whitePen, rect, ETextAlign.Center);

    const blueHighlight = new PdfPen(PdfColor.fromRgb(135, 206, 250), 16);
    page.drawLines(blueHighlight, [...points, points[0]]);
    page.drawLines(GUtil.basePen, [...points, points[0]]);
  }
}
